import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { REWARD_ICONS, type RewardIcon } from '../settings/SettingsContext';
import {
  fetchPreference,
  savePreference,
  type PreferenceView,
} from '../preferences/preferences';
import { useT } from '../i18n/i18n';
import { useLearner } from '../learners/LearnerContext';

function glyph(id: string): string {
  return REWARD_ICONS[id as RewardIcon] ?? '⭐';
}

function gridClass(n: number): string {
  if (n <= 4) return 'cols-2';
  if (n <= 9) return 'cols-3';
  return 'cols-4';
}

/**
 * Ask the learner which they like, one at a time.
 *
 * WHAT THEY PICK IS TAKEN AWAY BEFORE THE NEXT GO. Asking "which is your favourite" once gives
 * a single answer and no order; removing each choice as it is made turns the same taps into a
 * ranking, which is the whole point of doing it this way.
 */
export default function PreferencePage() {
  /* From the header rather than read once at mount: changing learner up there while this is
     open would otherwise leave it ranking rewards for the previous one without saying so. */
  const t = useT();
  const { learnerId } = useLearner();
  const [view, setView] = useState<PreferenceView | null>(null);
  const [remaining, setRemaining] = useState<string[]>([]);
  const [picked, setPicked] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const v = await fetchPreference(learnerId);
      setView(v);
      setRemaining(v.choices);
      setPicked([]);
      setDone(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Could not start that just now.'));
    }
  }, [learnerId]);

  useEffect(() => { void load(); }, [load]);

  async function finish(order: string[]) {
    if (!view) return;
    setSaving(true);
    setError(null);
    try {
      setView(await savePreference(view.learnerId, order));
      setDone(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  function choose(choice: string) {
    const nextPicked = [...picked, choice];
    const nextRemaining = remaining.filter((c) => c !== choice);
    setPicked(nextPicked);
    setRemaining(nextRemaining);
    /* The last one standing needs no tap: there is nothing to choose between. */
    if (nextRemaining.length <= 1) {
      void finish([...nextPicked, ...nextRemaining]);
    }
  }

  if (error && !view) return <div className="error" role="alert">{error}</div>;
  if (!view) return <p className="muted">{t('Loading…')}</p>;

  return (
    <section aria-labelledby="pref-h">
      <div className="week-bar">
        <h2 id="pref-h" className="week-title">Which reward do you like best?</h2>
        <div className="toolbar">
          <button className="btn secondary" onClick={() => void load()} disabled={saving}>
            {t('Start again')}
          </button>
          <Link className="btn secondary" to="/">{t('Done')}</Link>
        </div>
      </div>

      {error && <div className="error" role="alert">{error}</div>}

      {done ? (
        <div className="card">
          <h2>Thank you</h2>
          <p>
            <span style={{ fontSize: '2em' }} aria-hidden="true">
              {view.favourite ? glyph(view.favourite) : ''}
            </span>
          </p>
          <p className="field-hint">
            {t("This learner's rewards will use this one from now on. You can do it again at any time, and the new order replaces this one.")}
          </p>
          <ol className="pref-order">
            {view.ranking.map((c) => (
              <li key={c}><span aria-hidden="true">{glyph(c)}</span> {c}</li>
            ))}
          </ol>
        </div>
      ) : (
        <>
          <div className="card">
            <p className="field-hint" style={{ marginTop: 0 }}>
              Tap the reward you like best. It will be taken away, and you can pick again from
              the ones that are left, until they are all in order.
            </p>
          </div>
          <div
            className={`tile-grid ${gridClass(remaining.length)}`}
            role="group"
            aria-label="Rewards to choose from"
          >
            {remaining.map((choice) => (
              <button
                key={choice}
                className="tile"
                onClick={() => choose(choice)}
                disabled={saving}
                aria-label={choice}
              >
                <span style={{ fontSize: '2.2em' }} aria-hidden="true">{glyph(choice)}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
